/**
 * Predicate helpers for detecting terminal failure states on a message part.
 *
 * The actual shape is `MessagePartStatus` from `@assistant-ui/core` — see
 * the structural type below. We accept the structural type so these predicates
 * can be used in plain reducers without dragging the full assistant-ui type
 * graph in.
 */
type PartStatusLike = { type?: string; reason?: string } | undefined;

/** True when a part's status indicates a terminal error. */
export function isPartStatusError(status: PartStatusLike): boolean {
  return status?.type === 'incomplete' && status.reason === 'error';
}

/** True when a part's status indicates the part was cancelled. */
export function isPartStatusCancelled(status: PartStatusLike): boolean {
  return status?.type === 'incomplete' && status.reason === 'cancelled';
}

/**
 * Whether a message part is a tool-call in a terminal failure state — either
 * `isError: true` (set by the AI SDK when JSON-parse / input-validation fails)
 * or `status.type === 'incomplete'` with reason `error` or `cancelled`.
 *
 * Pure function; safe to call outside a hook context (e.g. inside the
 * message-parts grouping reducer in `CollapsibleMessageParts`).
 *
 * Non-tool parts (text, reasoning, etc.) always return `false`.
 */
export function isErroredToolPart(part: { type: string; isError?: boolean; status?: PartStatusLike }): boolean {
  if (part.type !== 'tool-call') {
    return false;
  }
  if (part.isError === true) {
    return true;
  }
  return isPartStatusError(part.status) || isPartStatusCancelled(part.status);
}
